import { CompatClient } from '@stomp/stompjs';
import { createSelector } from 'reselect';
import { PRESESSION_PATH } from '../../constants/app-constants';
import { UserProfile } from '../../core/models/user-profile';
import { ThunkResult } from '../../core/usecases/shared/ThunkResult';
import { GlobalAction, NavbarState, NavigationOption, WsSubscriptionData } from '../reducers/global.reducer';
import { AppState } from '../store';

export const HEALER_LOGIN_ROUTE = '/healer/login';
export const HEALER_DASHBOARD_ROUTE = '/healer/dashboard';
export const HEALER_AVAILABILITY_ROUTE = '/healer/availability';
export const HEALER_SCHEDULE_SESSION_ROUTE = '/healer/schedule-session';
export const HEALER_SESSIONS_ROUTE = '/healer/sessions';
export const HEALER_SETTINGS_ROUTE = '/healer/settings';
export const HEALER_PROFILE_ROUTE = '/healer/profile';
export const HEALER_MUSIC_PLAYGROUND_ROUTE = '/healer/playground/music';
export const HEALER_COLOR_PLAYGROUND_ROUTE = '/healer/playground/heallo-colors';
export const HEALER_BANK_DETAILS_ROUTE = '/healer/bank-details';

export const CLIENT_HOME_ROUTE = '/dashboard';
export const CLIENT_BOOKED_APPOINTMENTS_ROUTE = '/booked-appointments';
export const CLIENT_HEALERS_ROUTE = '/my-healers';
export const CLIENT_SETTINGS_ROUTE = '/settings';
export const CLIENT_DETAILS_ROUTE = '/healer/sessions/client';

export const REQUEST_NEW_HEALER_ROUTE = '/request-new-healer';

export const selectIsMobileUser = ({ global }: AppState): boolean => {
  return global.isMobileUser;
};
export const selectWsClient = ({ global }: AppState): CompatClient | undefined => {
  return global.ws.websocketClient;
};

export const selectIsMusicEnabled = ({ global }: AppState): boolean => {
  return global.musicPlayer?.musicEnabled && global.musicPlayer?.selectedSoundtrack !== undefined && global?.navbarState !== 'HIDDEN';
};

export const selectSoundtrackVolume = ({ global }: AppState): number => {
  return global.musicPlayer.soundtrackVolume;
};

export const selectIsNavbarVisible = ({ global }: AppState): boolean => {
  return global.navbarState !== 'HIDDEN';
};

export const selectAllActiveSubscriptions = ({ global }: AppState): any[] => {
  return [global.ws.subscriptions?.healloSubscription, global.ws.subscriptions?.musicSubscription].filter((sub) => sub !== undefined);
};

export const selectSoundtrack = ({ global }: AppState): string | undefined => {
  return global.musicPlayer.selectedSoundtrack;
};

export const selectHealloSubscription = ({ global }: AppState): WsSubscriptionData | undefined => {
  return global.ws.subscriptions.healloSubscription;
};

export const selectIsVolumeSliderVisible = ({ global }: AppState): boolean => {
  return global.navbarState === 'VISIBLE_WITH_VOLUME_SLIDER';
};

export const selectIsHealer = ({ global }: AppState): boolean => {
  return global.isHealer;
};

export const selectShowSoundIcon = ({ global }: AppState): boolean => {
  return global.isHealer ? true : !!global.currentUrl?.includes(PRESESSION_PATH);
};
export const selectWsConnected = ({ global }: AppState): boolean => {
  return global.ws.connected;
};

export const selectCurrentUserId = ({ global }: AppState): string | undefined => {
  return global.currentUser?.id;
};

export const selectCurrentUser = ({ global }: AppState): UserProfile | undefined => {
  return global.currentUser;
};
export const selectNavbarState = ({ global }: AppState): NavbarState => {
  return global.navbarState;
};

export const selectAvailableNavigationOptionsForClient = createSelector(
  (state: AppState) => state.global,
  (global): NavigationOption[] => {
    if (global.currentUser?.id == null && global.navbarState !== 'VISIBLE_PROSPECT_MODE') {
      return [];
    } else if (global.navbarState === 'VISIBLE_WITH_NAVIGATION_OPTIONS') {
      return ['HOME', 'BOOKED_APPOINTMENTS', 'MY_HEALERS', 'REQUEST_NEW_HEALER', 'MY_SELECTIONS', 'SETTINGS', 'CONTACT_US'] as NavigationOption[];
    } else if (global.navbarState === 'VISIBLE_PROSPECT_MODE') {
      return ['CONTACT_US'];
    }
    return [];
  }
);

export const selectHealerSideNavbarActiveTab = ({
  global,
}: AppState): 'HOME' | 'SCHEDULE_SESSION' | 'AVAILABILITY' | 'HEALER_SESSIONS' | 'HEALER_SETTINGS' | 'HEALER_PROFILE' | 'BANK_DETAILS' | undefined => {
  if (
    global.currentUrl === HEALER_DASHBOARD_ROUTE ||
    global.currentUrl === HEALER_MUSIC_PLAYGROUND_ROUTE ||
    global.currentUrl === HEALER_COLOR_PLAYGROUND_ROUTE
  ) {
    return 'HOME';
  }
  if (global.currentUrl === HEALER_AVAILABILITY_ROUTE) {
    return 'AVAILABILITY';
  }
  if (global.currentUrl === HEALER_SCHEDULE_SESSION_ROUTE) {
    return 'SCHEDULE_SESSION';
  }
  if (global.currentUrl?.includes(CLIENT_DETAILS_ROUTE)) {
    return 'HEALER_SESSIONS';
  }
  if (global.currentUrl === HEALER_SESSIONS_ROUTE) {
    return 'HEALER_SESSIONS';
  }
  if (global.currentUrl === HEALER_SETTINGS_ROUTE) {
    return 'HEALER_SETTINGS';
  }
  if (global.currentUrl === HEALER_PROFILE_ROUTE) {
    return 'HEALER_PROFILE';
  }
  if (global.currentUrl === HEALER_BANK_DETAILS_ROUTE) {
    return 'BANK_DETAILS';
  }
  return undefined;
};

export const selectClientActiveTab = ({ global }: AppState): NavigationOption | undefined => {
  if (global.currentUrl?.includes(CLIENT_HOME_ROUTE)) {
    return 'HOME';
  }
  if (global.currentUrl?.includes(CLIENT_BOOKED_APPOINTMENTS_ROUTE)) {
    return 'BOOKED_APPOINTMENTS';
  }
  if (global.currentUrl?.includes(CLIENT_HEALERS_ROUTE) || global.currentUrl?.includes(REQUEST_NEW_HEALER_ROUTE)) {
    return 'MY_HEALERS';
  }
  if (global.currentUrl?.includes(CLIENT_SETTINGS_ROUTE)) {
    return 'SETTINGS';
  }
  return undefined;
};

export const selectShowHealerSideNavbar = ({ global }: AppState): boolean => {
  return global.navbarState === 'HEALER_SIDE_NAVBAR';
};

export const selectShouldLoadCurrentUserProfile = ({ global }: AppState): boolean => {
  return global.currentUser?.email == null || global.currentUser?.id == null;
};

export const selectScreenOrientation = ({ global }: AppState) => global.screenOrientation;

export const enableMusic = () => ({ type: 'SET_MUSIC_ENABLED', payload: true } as GlobalAction);
export const disableMusic = () => ({ type: 'SET_MUSIC_ENABLED', payload: false } as GlobalAction);
export const setCurrentUrl = (url: string) => ({ type: 'SET_CURRENT_URL', payload: url } as GlobalAction);
export const setWsClient = (client: CompatClient) => ({ type: 'SET_WS_CLIENT', payload: client } as GlobalAction);
export const markWsAsConnected = () => ({ type: 'SET_WS_CONNECTED', payload: true } as GlobalAction);
export const markWsAsDisconnected = () => ({ type: 'SET_WS_CONNECTED', payload: true } as GlobalAction);
export const showNavbar = (navbarState: NavbarState) => ({ type: 'SHOW_NAVBAR', payload: navbarState } as GlobalAction);
export const hideNavbar = () => ({ type: 'HIDE_NAVBAR' } as GlobalAction);
export const setSoundtrack = (soundtrack?: string) => ({ type: 'SET_SELECTED_SOUNDTRACK', payload: soundtrack } as GlobalAction);
export const setSoundtrackVolume = (volume?: number) => ({ type: 'SET_SOUNDTRACK_VOLUME_LEVEL', payload: volume } as GlobalAction);
export const setIsHealer = (isHealer: boolean) => ({ type: 'SET_IS_HEALER', payload: isHealer } as GlobalAction);
export const setLoggedUserProfileId = (id: string) => ({ type: 'SET_CURRENT_USER_ID', payload: id } as GlobalAction);
export const setLoggedUserProfile = (userProfile: UserProfile) => ({ type: 'SET_CURRENT_USER', payload: userProfile } as GlobalAction);
export const setIsMobile = (isMobile: boolean) => ({ type: 'SET_IS_MOBILE_USER', payload: isMobile } as GlobalAction);
export const setScreenOrientation = (orientation: 'PORTRAIT' | 'LANDSCAPE') => ({ type: 'SET_SCREEN_ORIENTATION', payload: orientation } as GlobalAction);
export const setHealloSubscription = (subscriptionId: string, resubscribeAction: ThunkResult<void>) =>
  ({ type: 'SET_HEALLO_SUBSCRIPTION', payload: { id: subscriptionId, resubscribeAction } } as GlobalAction);
